import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Save, Globe, Palette, Search, BarChart3, Share2, Phone,
  Layout as LayoutIcon, Server, ShieldCheck, Wrench, CheckCircle2, Webhook, Send, Mail, Inbox, PlugZap,
} from 'lucide-react';
import { toast } from 'sonner';
import { api, unwrap } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Label, Textarea } from '@/components/ui/Input';
import { cn } from '@/lib/cn';

type SettingsGroup =
  | 'general' | 'brand' | 'seo' | 'analytics' | 'social'
  | 'contact' | 'footer' | 'email' | 'email_branding' | 'acelle' | 'integrations' | 'security' | 'maintenance';

type AllSettings = Record<SettingsGroup, Record<string, string | null>>;

type Field = {
  key: string;
  label: string;
  type?: 'text' | 'email' | 'textarea' | 'select' | 'password' | 'color' | 'number';
  placeholder?: string;
  help?: string;
  options?: { value: string; label: string }[];
  full?: boolean;
};

type Section = {
  id: SettingsGroup;
  label: string;
  icon: ReactNode;
  navGroup: string;
  title: string;
  description: string;
  fields: Field[];
};

const BOOL_OPTS = [
  { value: 'false', label: 'Desactivado' },
  { value: 'true', label: 'Activado' },
];

const SECTIONS: Section[] = [
  {
    id: 'general', label: 'General', icon: <Globe className="size-4" />, navGroup: 'Sitio',
    title: 'General', description: 'Identidad básica del sitio.',
    fields: [
      { key: 'site_name', label: 'Nombre del sitio', placeholder: 'Mi Empresa' },
      { key: 'site_tagline', label: 'Tagline' },
      { key: 'site_url', label: 'URL del sitio', placeholder: 'https://misitio.com' },
      { key: 'admin_email', label: 'Email del administrador', type: 'email' },
      { key: 'timezone', label: 'Zona horaria', placeholder: 'Europe/Madrid' },
      { key: 'locale', label: 'Idioma por defecto', placeholder: 'es' },
    ],
  },
  {
    id: 'brand', label: 'Marca', icon: <Palette className="size-4" />, navGroup: 'Sitio',
    title: 'Marca e identidad', description: 'Logos e imágenes por defecto del sitio.',
    fields: [
      { key: 'logo_url', label: 'Logo (URL)', full: true },
      { key: 'logo_dark_url', label: 'Logo fondo oscuro (URL)', full: true },
      { key: 'favicon_url', label: 'Favicon (URL)' },
      { key: 'og_default_image', label: 'Imagen social por defecto (URL)' },
    ],
  },
  {
    id: 'seo', label: 'SEO', icon: <Search className="size-4" />, navGroup: 'Sitio',
    title: 'SEO por defecto', description: 'Aplicado a páginas sin SEO propio.',
    fields: [
      { key: 'default_title', label: 'Título por defecto', full: true },
      { key: 'title_template', label: 'Plantilla de título', placeholder: '%s · Mi Empresa', help: 'Usa %s donde va el título de la página.' },
      { key: 'robots', label: 'Robots', placeholder: 'index,follow' },
      { key: 'default_description', label: 'Descripción por defecto', type: 'textarea', full: true },
      { key: 'default_og_image', label: 'Imagen OG por defecto (URL)', full: true },
      { key: 'twitter_handle', label: 'Usuario de X/Twitter', placeholder: '@miempresa' },
      { key: 'gsc_verification', label: 'Verificación Search Console', help: 'Contenido del meta google-site-verification.' },
    ],
  },
  {
    id: 'analytics', label: 'Analítica', icon: <BarChart3 className="size-4" />, navGroup: 'Sitio',
    title: 'Analítica y scripts', description: 'Etiquetas de medición y scripts personalizados que se inyectan en el sitio.',
    fields: [
      { key: 'ga4_id', label: 'Google Analytics 4 (Measurement ID)', placeholder: 'G-XXXXXXXXXX' },
      { key: 'gtm_id', label: 'Google Tag Manager (Container ID)', placeholder: 'GTM-XXXXXXX' },
      { key: 'meta_pixel_id', label: 'Meta Pixel ID', placeholder: '000000000000000' },
      { key: 'head_scripts', label: 'Scripts en <head>', type: 'textarea', full: true, help: 'HTML/JS inyectado al final del <head>. Úsalo con cuidado.' },
      { key: 'body_scripts', label: 'Scripts al final del <body>', type: 'textarea', full: true },
    ],
  },
  {
    id: 'social', label: 'Redes', icon: <Share2 className="size-4" />, navGroup: 'Sitio',
    title: 'Redes sociales', description: 'URLs completas a cada perfil.',
    fields: [
      { key: 'linkedin', label: 'LinkedIn' },
      { key: 'instagram', label: 'Instagram' },
      { key: 'twitter', label: 'X / Twitter' },
      { key: 'youtube', label: 'YouTube' },
      { key: 'facebook', label: 'Facebook' },
      { key: 'tiktok', label: 'TikTok' },
    ],
  },
  {
    id: 'contact', label: 'Contacto', icon: <Phone className="size-4" />, navGroup: 'Sitio',
    title: 'Datos de contacto', description: 'Se muestran en el footer y páginas de contacto.',
    fields: [
      { key: 'phone', label: 'Teléfono' },
      { key: 'whatsapp', label: 'WhatsApp', placeholder: '+34600000000' },
      { key: 'email', label: 'Email', type: 'email' },
      { key: 'hours', label: 'Horario', placeholder: 'L-V 9:00-18:00' },
      { key: 'address', label: 'Dirección', type: 'textarea', full: true },
      { key: 'maps_url', label: 'Google Maps (URL embed)', full: true },
    ],
  },
  {
    id: 'footer', label: 'Footer', icon: <LayoutIcon className="size-4" />, navGroup: 'Sitio',
    title: 'Footer', description: 'Texto y enlaces legales del pie.',
    fields: [
      { key: 'copyright', label: 'Copyright', full: true },
      { key: 'legal_links', label: 'Enlaces legales', type: 'textarea', full: true, placeholder: 'Aviso legal|/aviso-legal\nPrivacidad|/privacidad', help: 'Uno por línea con el formato Texto|URL.' },
    ],
  },
  {
    id: 'email', label: 'Email y SMTP', icon: <Server className="size-4" />, navGroup: 'Comunicación',
    title: 'Email y transporte', description: 'Remitente, avisos y servidor de envío de los formularios.',
    fields: [
      { key: 'email_from_name', label: 'Nombre del remitente', placeholder: 'Mi Empresa' },
      { key: 'email_from_email', label: 'Email del remitente', type: 'email', placeholder: 'hola@misitio.com' },
      { key: 'email_reply_to', label: 'Responder a (Reply-To)', type: 'email' },
      { key: 'email_admin_notify', label: 'Recibe los avisos', type: 'email', placeholder: 'info@misitio.com' },
      { key: 'email_bcc', label: 'Copia oculta (BCC)', type: 'email', placeholder: 'Opcional' },
      {
        key: 'email_notify_mode', label: 'Al recibir un formulario', type: 'select', full: true,
        options: [
          { value: 'both', label: 'Avisar a tu equipo y enviar acuse al cliente (recomendado)' },
          { value: 'admin', label: 'Solo avisar a tu equipo (el cliente no recibe correo)' },
          { value: 'client', label: 'Solo enviar acuse al cliente' },
        ],
        help: 'Controla quién recibe email cuando alguien rellena un formulario de contacto o se suscribe.',
      },
      {
        key: 'email_sandbox', label: 'Modo pruebas (sandbox)', type: 'select',
        options: [
          { value: 'false', label: 'Desactivado — se envían los emails' },
          { value: 'true', label: 'Activado — no se envía nada (solo log)' },
        ],
        help: 'En producción debe estar desactivado.',
      },
      {
        key: 'mail_driver', label: 'Transporte de envío', type: 'select', full: true,
        options: [
          { value: '', label: 'Por defecto (configuración del servidor)' },
          { value: 'smtp', label: 'SMTP propio' },
          { value: 'resend', label: 'Resend (API)' },
          { value: 'log', label: 'Log (no envía, solo registra)' },
        ],
        help: 'Elige cómo se entregan los emails. SMTP usa los campos de abajo.',
      },
      { key: 'smtp_host', label: 'SMTP host', placeholder: 'smtp.tuservidor.com' },
      { key: 'smtp_port', label: 'SMTP puerto', placeholder: '587' },
      { key: 'smtp_username', label: 'SMTP usuario' },
      { key: 'smtp_password', label: 'SMTP contraseña', type: 'password' },
      {
        key: 'smtp_encryption', label: 'Cifrado', type: 'select',
        options: [
          { value: 'tls', label: 'TLS' },
          { value: 'ssl', label: 'SSL' },
          { value: '', label: 'Ninguno' },
        ],
      },
      { key: 'resend_key', label: 'Resend API Key', type: 'password', placeholder: 're_********', help: 'Solo si usas Resend como transporte.' },
    ],
  },
  {
    id: 'email_branding', label: 'Correos (marca)', icon: <Mail className="size-4" />, navGroup: 'Comunicación',
    title: 'Branding de los correos', description: 'Personaliza el logo y los colores de los emails que reciben tus clientes y tu equipo.',
    fields: [
      { key: 'email_logo_url', label: 'Logo del correo (URL)', full: true, placeholder: 'https://misitio.com/logo-email.png', help: 'Se muestra en la cabecera. Si lo dejas vacío, se usa el logo del sitio o el nombre de la marca. Recomendado: PNG sobre fondo transparente, ~280×80px.' },
      { key: 'email_header_bg', label: 'Fondo de la cabecera', type: 'color', full: true, placeholder: '#2563eb', help: 'Elige un color sólido con el selector. Para un degradado, escribe el CSS en el campo (p. ej. linear-gradient(90deg,#1e3a8a,#2563eb)). Vacío = se usa el color de acento.' },
      { key: 'email_accent_color', label: 'Color de acento', type: 'color', placeholder: '#2563eb', help: 'Enlaces de los correos.' },
      { key: 'email_footer_note', label: 'Nota del pie', type: 'textarea', full: true, placeholder: 'Mi Empresa · Dirección, teléfono…', help: 'Texto opcional que aparece sobre el copyright (datos de empresa, dirección…).' },
    ],
  },
  {
    id: 'acelle', label: 'Acelle (Newsletter)', icon: <Inbox className="size-4" />, navGroup: 'Comunicación',
    title: 'Acelle Mail', description: 'Exporta automáticamente a una lista de Acelle Mail cada email que se da de alta en la newsletter del sitio.',
    fields: [
      { key: 'acelle_enabled', label: 'Activar exportación a Acelle', type: 'select', options: BOOL_OPTS, help: 'Si está activo, cada alta de newsletter se envía también a Acelle.' },
      { key: 'acelle_endpoint', label: 'URL de tu Acelle', full: true, placeholder: 'https://mail.tudominio.com', help: 'Sin /api/v1 al final: solo el dominio base de tu instalación de Acelle.' },
      { key: 'acelle_api_token', label: 'API Token', type: 'password', full: true, help: 'Lo encuentras en Acelle → tu perfil → API token.' },
      { key: 'acelle_tag', label: 'Etiqueta por defecto (opcional)', placeholder: 'web, newsletter', help: 'Etiquetas separadas por comas para las altas sin regla específica.' },
      {
        key: 'acelle_double_optin', label: 'Confirmación (doble opt-in)', type: 'select',
        options: [
          { value: 'false', label: 'No — se añade ya suscrito (subscribed)' },
          { value: 'true', label: 'Sí — queda pendiente de confirmar (unconfirmed)' },
        ],
        help: 'Debe coincidir con la configuración de opt-in de tu lista en Acelle.',
      },
    ],
  },
  {
    id: 'integrations', label: 'Integraciones / CRM', icon: <Webhook className="size-4" />, navGroup: 'Plataforma',
    title: 'CRM externo', description: 'Reenvía cada lead (contacto, newsletter, formularios y chatbot) a tu CRM mediante un webhook firmado. Revisa el estado de los envíos en la sección «Integraciones».',
    fields: [
      { key: 'crm_enabled', label: 'Activar envío al CRM', type: 'select', options: BOOL_OPTS, help: 'Interruptor general. Si está desactivado no se envía nada (el lead se sigue guardando en el CMS).' },
      { key: 'crm_endpoint', label: 'URL del endpoint (webhook) del CRM', full: true, placeholder: 'https://tu-crm.com/api/leads' },
      { key: 'crm_auth_header', label: 'Cabecera de autenticación (nombre)', placeholder: 'Authorization' },
      { key: 'crm_auth_value', label: 'Valor de la cabecera', type: 'password', placeholder: 'Bearer xxxxxxxx', help: 'Token/clave que tu CRM espera. Se envía en cada petición.' },
      { key: 'crm_secret', label: 'Secreto de firma (HMAC)', type: 'password', full: true, help: 'Opcional. Si lo defines, firmamos el cuerpo en la cabecera X-Sytel-Signature (sha256) para que tu CRM verifique el origen.' },
      { key: 'crm_send_contact', label: 'Enviar leads de Contacto', type: 'select', options: BOOL_OPTS },
      { key: 'crm_send_newsletter', label: 'Enviar altas de Newsletter', type: 'select', options: BOOL_OPTS },
      { key: 'crm_send_forms', label: 'Enviar envíos de Formularios', type: 'select', options: BOOL_OPTS },
      { key: 'crm_send_ai', label: 'Enviar leads del Chatbot IA', type: 'select', options: BOOL_OPTS },
    ],
  },
  {
    id: 'security', label: 'Seguridad', icon: <ShieldCheck className="size-4" />, navGroup: 'Plataforma',
    title: 'Seguridad anti-bots y abuso', description: 'Captcha y límites de envío por IP para proteger los formularios públicos.',
    fields: [
      { key: 'turnstile_enabled', label: 'Activar Turnstile', type: 'select', options: BOOL_OPTS, help: 'Cuando está activo, los formularios exigen el captcha.' },
      { key: 'turnstile_site_key', label: 'Site Key (pública)', full: true },
      { key: 'turnstile_secret_key', label: 'Secret Key (privada)', type: 'password', full: true },
      {
        key: 'ratelimit_enabled', label: 'Límite de envíos por IP', type: 'select', options: BOOL_OPTS, full: true,
        help: 'Limita cuántas veces puede enviarse un formulario desde una misma IP. En desarrollo local no se aplica nunca, para no entorpecer las pruebas.',
      },
      { key: 'ratelimit_form_per_min', label: 'Formularios · máx. por minuto', type: 'number', placeholder: '8', help: 'Contacto, newsletter y formularios. 0 = sin tope por minuto.' },
      { key: 'ratelimit_form_per_hour', label: 'Formularios · máx. por hora', type: 'number', placeholder: '40', help: '0 = sin tope por hora.' },
      { key: 'ratelimit_chat_per_min', label: 'Chatbot · máx. por minuto', type: 'number', placeholder: '20' },
      { key: 'ratelimit_chat_per_hour', label: 'Chatbot · máx. por hora', type: 'number', placeholder: '120' },
    ],
  },
  {
    id: 'maintenance', label: 'Mantenimiento', icon: <Wrench className="size-4" />, navGroup: 'Plataforma',
    title: 'Mantenimiento y visibilidad', description: 'Modo mantenimiento e indexación global del sitio.',
    fields: [
      { key: 'maintenance_mode', label: 'Modo mantenimiento', type: 'select', options: BOOL_OPTS, help: 'Muestra una página de mantenimiento a los visitantes.' },
      { key: 'noindex_global', label: 'Bloquear indexación (noindex)', type: 'select', options: BOOL_OPTS, help: 'Útil mientras el sitio está en pruebas.' },
      { key: 'maintenance_message', label: 'Mensaje de mantenimiento', type: 'textarea', full: true },
    ],
  },
];

export const Route = createFileRoute('/_app/settings')({
  component: SettingsPage,
});

function SettingsPage() {
  const qc = useQueryClient();
  const [active, setActive] = useState<SettingsGroup>('general');

  const { data, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => unwrap<AllSettings>(api.get('/settings')),
  });

  const [values, setValues] = useState<AllSettings | null>(null);
  useEffect(() => { if (data && !values) setValues(data); }, [data, values]);

  const save = useMutation({
    mutationFn: async (group: SettingsGroup) => {
      if (!values) return;
      return api.put('/settings', { group, values: values[group] });
    },
    onSuccess: () => {
      toast.success('Ajustes guardados');
      void qc.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: () => toast.error('No se pudo guardar'),
  });

  function update(group: SettingsGroup, key: string, value: string) {
    setValues((v) => (v ? { ...v, [group]: { ...v[group], [key]: value } } : v));
  }

  const navGroups = useMemo(() => {
    const map = new Map<string, Section[]>();
    for (const s of SECTIONS) {
      const arr = map.get(s.navGroup) ?? [];
      arr.push(s);
      map.set(s.navGroup, arr);
    }
    return Array.from(map.entries());
  }, []);

  if (isLoading || !values) {
    return <div className="p-8 text-sm text-neutral-500">Cargando ajustes…</div>;
  }

  const section = SECTIONS.find((s) => s.id === active)!;

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6 md:p-8">
      <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Ajustes del sitio</h1>
          <p className="text-sm text-neutral-500">Configuración global de SEO, analítica, comunicación y seguridad.</p>
        </div>
        <span className="hidden items-center gap-1.5 text-xs text-neutral-400 sm:inline-flex">
          <CheckCircle2 className="size-3.5 text-emerald-500" />
          Cambios guardados por sección
        </span>
      </header>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[240px_1fr]">
        <aside className="self-start rounded-2xl border border-neutral-200 bg-white p-3 lg:sticky lg:top-4">
          <nav className="space-y-4">
            {navGroups.map(([group, items]) => (
              <div key={group}>
                <p className="mb-1.5 px-3 text-[10px] font-bold uppercase tracking-wider text-neutral-400">{group}</p>
                <ul className="space-y-0.5">
                  {items.map((item) => {
                    const isActive = active === item.id;
                    return (
                      <li key={item.id}>
                        <button
                          type="button"
                          onClick={() => setActive(item.id)}
                          className={cn(
                            'flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                            isActive ? 'text-white' : 'text-neutral-600 hover:bg-neutral-100',
                          )}
                          style={isActive ? { background: 'linear-gradient(90deg, #66023C 0%, #AD026A 100%)' } : undefined}
                        >
                          {item.icon}
                          {item.label}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </nav>
        </aside>

        <div className="min-w-0">
          <SectionCard
            title={section.title}
            description={section.description}
            onSave={() => save.mutate(section.id)}
            saving={save.isPending}
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {section.fields.map((field) => (
                <FieldControl
                  key={field.key}
                  field={field}
                  value={normalize(values[section.id]?.[field.key], field)}
                  onChange={(v) => update(section.id, field.key, v)}
                />
              ))}
            </div>
            {section.id === 'email' ? (
              <EmailTestPanel defaultTo={values.email?.email_admin_notify ?? ''} />
            ) : null}
            {section.id === 'acelle' ? (
              <>
                <AcelleRoutingPanel
                  acelle={(values.acelle ?? {}) as Record<string, unknown>}
                  onPatch={(partial) =>
                    setValues((v) => (v ? ({ ...v, acelle: { ...v.acelle, ...partial } } as AllSettings) : v))
                  }
                />
                <AcelleTestPanel />
              </>
            ) : null}
          </SectionCard>
        </div>
      </div>
    </div>
  );
}

function normalize(raw: string | null | undefined, field: Field): string {
  if (field.type === 'select' && field.options === BOOL_OPTS) {
    // Los reenvíos por tipo de lead vienen activados por defecto (igual que el backend).
    if ((raw === null || raw === undefined) && field.key.startsWith('crm_send_')) {
      return 'true';
    }
    return raw === 'true' || raw === '1' ? 'true' : 'false';
  }
  return raw ?? '';
}

function SectionCard({ title, description, children, onSave, saving }: {
  title: string; description: string; children: ReactNode; onSave: () => void; saving: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {children}
        <div className="pt-2">
          <Button onClick={onSave} disabled={saving}>
            <Save className="size-4" /> {saving ? 'Guardando…' : 'Guardar cambios'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function EmailTestPanel({ defaultTo }: { defaultTo: string }) {
  const [to, setTo] = useState(defaultTo);
  useEffect(() => { setTo(defaultTo); }, [defaultTo]);

  const test = useMutation({
    mutationFn: async () => {
      const res = await api.post('/settings/email/test', { to: to.trim() || undefined });
      return res.data as { message?: string; data?: { driver?: string } };
    },
    onSuccess: (res) => {
      const isLog = res.data?.driver === 'log';
      const message = res.message ?? 'Email de prueba enviado.';
      if (isLog) toast.warning(message);
      else toast.success(message);
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'No se pudo enviar el email de prueba.';
      toast.error(message);
    },
  });

  return (
    <div className="rounded-xl border border-dashed border-neutral-300 bg-neutral-50 p-4">
      <div className="flex items-start gap-2">
        <Send className="mt-0.5 size-4 shrink-0 text-neutral-500" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-neutral-800">Probar el envío</p>
          <p className="mb-3 text-xs text-neutral-500">
            Guarda los cambios primero: la prueba usa la configuración <strong>ya guardada</strong>.
            Ignora el modo sandbox para comprobar la entrega real.
          </p>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
            <div className="flex-1 space-y-1.5">
              <Label htmlFor="email_test_to">Enviar prueba a</Label>
              <Input
                id="email_test_to"
                type="email"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="tucorreo@dominio.com"
              />
            </div>
            <Button type="button" variant="secondary" onClick={() => test.mutate()} disabled={test.isPending}>
              <Send className="size-4" /> {test.isPending ? 'Enviando…' : 'Enviar email de prueba'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

type AcelleRoute = { source: string; list_uid: string; tag?: string };
type AcelleList = { uid: string; name: string; subscribers?: number };

const SELECT_CLS = 'flex h-9 w-full rounded-md border border-neutral-200 bg-white px-3 text-sm';

/** Normaliza el valor guardado de acelle_routes (array | JSON string | null). */
function parseRoutes(raw: unknown): AcelleRoute[] {
  let val = raw;
  if (typeof val === 'string') {
    try { val = JSON.parse(val); } catch { return []; }
  }
  if (!Array.isArray(val)) return [];
  return val
    .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object')
    .map((r) => ({
      source: String(r.source ?? ''),
      list_uid: String(r.list_uid ?? ''),
      tag: r.tag != null ? String(r.tag) : '',
    }));
}

const SOURCE_HINTS = ['home', 'blog', 'popup', 'contacto'];

function asBool(v: unknown): boolean {
  return v === true || v === 'true' || v === 1 || v === '1';
}
function asStringArray(v: unknown): string[] {
  let val = v;
  if (typeof val === 'string') {
    try { val = JSON.parse(val); } catch { return []; }
  }
  return Array.isArray(val) ? val.map((x) => String(x)).filter(Boolean) : [];
}

function AcelleRoutingPanel({ acelle, onPatch }: {
  acelle: Record<string, unknown>;
  onPatch: (partial: Record<string, unknown>) => void;
}) {
  const defaultList = (acelle.acelle_list_uid as string) ?? '';
  const routes = parseRoutes(acelle.acelle_routes);
  const syncInEnabled = asBool(acelle.acelle_sync_in_enabled);
  const syncLists = asStringArray(acelle.acelle_sync_lists);

  const [lists, setLists] = useState<AcelleList[]>([]);

  const load = useMutation({
    mutationFn: () => unwrap<AcelleList[]>(api.get('/settings/acelle/lists')),
    onSuccess: (data) => {
      setLists(data);
      toast.success(`${data.length} lista(s) cargada(s) desde Acelle.`);
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'No se pudieron cargar las listas. Guarda la URL y el token primero.';
      toast.error(message);
    },
  });

  const syncIn = useMutation({
    mutationFn: () => api.post('/settings/acelle/sync-in').then((r) => r.data as { message?: string }),
    onSuccess: (res) => toast.success(res.message ?? 'Sincronización entrante completada.'),
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'No se pudo sincronizar desde Acelle.';
      toast.error(message);
    },
  });

  // Mantiene en el desplegable cualquier UID ya guardado aunque aún no se hayan
  // cargado las listas desde Acelle (para no perder la selección).
  const knownUids = new Set(lists.map((l) => l.uid));
  const extraUids = [defaultList, ...routes.map((r) => r.list_uid)]
    .filter((u) => u && !knownUids.has(u));
  const options: AcelleList[] = [
    ...lists,
    ...Array.from(new Set(extraUids)).map((uid) => ({ uid, name: `${uid} (no cargada)` })),
  ];

  const ListSelect = ({ value, onChange, allowGlobal }: {
    value: string; onChange: (v: string) => void; allowGlobal?: boolean;
  }) => (
    <select className={SELECT_CLS} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{allowGlobal ? '— Lista por defecto —' : '— Elige una lista —'}</option>
      {options.map((l) => (
        <option key={l.uid} value={l.uid}>{l.name}</option>
      ))}
    </select>
  );

  function setRoutes(next: AcelleRoute[]) {
    onPatch({ acelle_routes: next });
  }
  function updateRoute(i: number, patch: Partial<AcelleRoute>) {
    setRoutes(routes.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }

  return (
    <div className="space-y-4 rounded-xl border border-neutral-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-neutral-800">Listas y enrutado por origen</p>
          <p className="text-xs text-neutral-500">
            Elige a qué lista de Acelle va cada origen de la newsletter. Lo que no tenga
            regla cae en la lista por defecto.
          </p>
        </div>
        <Button type="button" variant="secondary" onClick={() => load.mutate()} disabled={load.isPending}>
          <Inbox className="size-4" /> {load.isPending ? 'Cargando…' : 'Cargar listas'}
        </Button>
      </div>

      <div className="space-y-1.5">
        <Label>Lista por defecto</Label>
        <ListSelect value={defaultList} onChange={(v) => onPatch({ acelle_list_uid: v })} />
        <p className="text-xs text-neutral-400">
          Destino de las altas sin regla específica. Pulsa «Cargar listas» para ver las opciones.
        </p>
      </div>

      <div className="space-y-2">
        <Label>Reglas origen → lista</Label>
        <datalist id="acelle-source-hints">
          {SOURCE_HINTS.map((s) => <option key={s} value={s} />)}
        </datalist>
        {routes.length === 0 ? (
          <p className="text-xs text-neutral-400">
            Sin reglas. Añade una para enviar, por ejemplo, las altas del blog a una lista distinta.
          </p>
        ) : null}
        {routes.map((route, i) => (
          <div key={i} className="grid grid-cols-1 gap-2 rounded-lg border border-neutral-100 bg-neutral-50 p-2 sm:grid-cols-[1fr_1fr_0.8fr_auto]">
            <Input
              list="acelle-source-hints"
              placeholder="Origen (home, blog…)"
              value={route.source}
              onChange={(e) => updateRoute(i, { source: e.target.value })}
            />
            <ListSelect value={route.list_uid} onChange={(v) => updateRoute(i, { list_uid: v })} />
            <Input
              placeholder="Etiqueta (opcional)"
              value={route.tag ?? ''}
              onChange={(e) => updateRoute(i, { tag: e.target.value })}
            />
            <Button
              type="button"
              variant="ghost"
              onClick={() => setRoutes(routes.filter((_, idx) => idx !== i))}
            >
              Quitar
            </Button>
          </div>
        ))}
        <Button
          type="button"
          variant="secondary"
          onClick={() => setRoutes([...routes, { source: '', list_uid: '', tag: '' }])}
        >
          + Añadir regla
        </Button>
      </div>

      <div className="space-y-3 border-t border-neutral-100 pt-4">
        <div>
          <p className="text-sm font-semibold text-neutral-800">Sincronización entrante (Acelle → CMS)</p>
          <p className="text-xs text-neutral-500">
            Cada hora trae de Acelle las <strong>bajas y rebotes</strong> y los aplica a tus
            suscriptores del CMS. No importa altas nuevas.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm font-medium text-neutral-800">
          <input
            type="checkbox"
            checked={syncInEnabled}
            onChange={(e) => onPatch({ acelle_sync_in_enabled: e.target.checked })}
          />
          Activar sincronización entrante
        </label>

        {syncInEnabled ? (
          <>
            <div className="space-y-1.5">
              <Label>Listas a vigilar</Label>
              {lists.length === 0 ? (
                <p className="text-xs text-neutral-400">
                  Pulsa «Cargar listas» arriba para elegirlas. Si no marcas ninguna, se vigilan
                  automáticamente las listas que ya usas para exportar.
                </p>
              ) : (
                <div className="flex flex-wrap gap-3">
                  {lists.map((l) => (
                    <label key={l.uid} className="flex items-center gap-1.5 text-sm text-neutral-700">
                      <input
                        type="checkbox"
                        checked={syncLists.includes(l.uid)}
                        onChange={(e) =>
                          onPatch({
                            acelle_sync_lists: e.target.checked
                              ? [...syncLists, l.uid]
                              : syncLists.filter((u) => u !== l.uid),
                          })
                        }
                      />
                      {l.name}
                    </label>
                  ))}
                </div>
              )}
            </div>
            <Button type="button" variant="secondary" onClick={() => syncIn.mutate()} disabled={syncIn.isPending}>
              <Inbox className="size-4" /> {syncIn.isPending ? 'Sincronizando…' : 'Sincronizar ahora'}
            </Button>
            <p className="text-xs text-neutral-400">
              «Sincronizar ahora» usa la configuración <strong>ya guardada</strong>. Guarda primero los cambios.
            </p>
          </>
        ) : null}
      </div>

      <p className="text-xs text-neutral-400">
        Recuerda pulsar «Guardar cambios» arriba para aplicar estas listas y reglas.
      </p>
    </div>
  );
}

function AcelleTestPanel() {
  const test = useMutation({
    mutationFn: async () => {
      const res = await api.post('/settings/acelle/test');
      return res.data as { message?: string };
    },
    onSuccess: (res) => toast.success(res.message ?? 'Conexión correcta con Acelle.'),
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'No se pudo conectar con Acelle.';
      toast.error(message);
    },
  });

  return (
    <div className="rounded-xl border border-dashed border-neutral-300 bg-neutral-50 p-4">
      <div className="flex items-start gap-2">
        <PlugZap className="mt-0.5 size-4 shrink-0 text-neutral-500" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-neutral-800">Probar la conexión</p>
          <p className="mb-3 text-xs text-neutral-500">
            Guarda los cambios primero: la prueba usa la configuración <strong>ya guardada</strong> y
            valida el token contra la lista indicada.
          </p>
          <Button type="button" variant="secondary" onClick={() => test.mutate()} disabled={test.isPending}>
            <PlugZap className="size-4" /> {test.isPending ? 'Comprobando…' : 'Probar conexión con Acelle'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function FieldControl({ field, value, onChange }: {
  field: Field; value: string; onChange: (v: string) => void;
}) {
  const span = field.full || field.type === 'textarea' ? 'sm:col-span-2' : '';
  return (
    <div className={cn('space-y-1.5', span)}>
      <Label htmlFor={field.key}>{field.label}</Label>
      {field.type === 'textarea' ? (
        <Textarea id={field.key} value={value} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} rows={4} />
      ) : field.type === 'color' ? (
        <div className="flex items-center gap-2">
          <input
            type="color"
            aria-label={`${field.label} (selector)`}
            value={/^#[0-9a-f]{6}$/i.test(value) ? value : '#AD026A'}
            onChange={(e) => onChange(e.target.value)}
            className="h-9 w-12 shrink-0 cursor-pointer rounded-md border border-neutral-200 bg-white p-1"
          />
          <Input id={field.key} value={value} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} />
        </div>
      ) : field.type === 'select' ? (
        <select
          id={field.key}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex h-9 w-full rounded-md border border-neutral-200 bg-white px-3 text-sm"
        >
          {field.options?.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : (
        <Input
          id={field.key}
          type={field.type === 'password' ? 'password' : field.type ?? 'text'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          autoComplete={field.type === 'password' ? 'new-password' : undefined}
        />
      )}
      {field.help ? <p className="text-xs text-neutral-400">{field.help}</p> : null}
    </div>
  );
}
